import { useEffect } from 'react';

// The areas that scroll on their own inside the page content (see .page-content in theme.css):
// the rows of a table that fills the page, and the body of a .page-fill card
const SCROLLERS = [
	':scope > * > .table-container > .react-bootstrap-table:not(.react-bootstrap-table--scrollable)',
	':scope > .page-fill .page-fill__scroll'
].join(', ');

// Marks the box around each scrolling area with whether content is hidden above or below it,
// so its header and footer can show a shadow
export function useScrollShadows(pageContentRef) {
	useEffect(() => {
		const pageContent = pageContentRef.current;

		if (!pageContent) {
			return undefined;
		}

		const trackedScrollers = new Set();

		const updateShadows = (scroller) => {
			const host = scroller.closest('.table-container, .page-fill');
			const hiddenBelow = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;

			if (host) {
				host.classList.toggle('has-more-above', scroller.scrollTop > 0);
				host.classList.toggle('has-more-below', hiddenBelow > 1);
			}
		};

		const onScroll = (event) => { updateShadows(event.currentTarget); };

		// Watches the scroller for a new height and its content for new rows or fields
		const resizeObserver = new ResizeObserver((entries) => {
			entries.forEach(({ target }) => {
				const scroller = trackedScrollers.has(target) ? target : target.parentElement;

				if (scroller) {
					updateShadows(scroller);
				}
			});
		});

		const trackScrollers = () => {
			pageContent.querySelectorAll(SCROLLERS).forEach((scroller) => {
				if (trackedScrollers.has(scroller)) {
					return;
				}

				trackedScrollers.add(scroller);
				scroller.addEventListener('scroll', onScroll);
				resizeObserver.observe(scroller);
				Array.from(scroller.children).forEach((child) => { resizeObserver.observe(child); });
				updateShadows(scroller);
			});

			trackedScrollers.forEach((scroller) => {
				if (!pageContent.contains(scroller)) {
					scroller.removeEventListener('scroll', onScroll);
					trackedScrollers.delete(scroller);
				}
			});
		};

		const mutationObserver = new MutationObserver(trackScrollers);

		mutationObserver.observe(pageContent, { childList: true, subtree: true });
		trackScrollers();

		return () => {
			mutationObserver.disconnect();
			resizeObserver.disconnect();
			trackedScrollers.forEach((scroller) => { scroller.removeEventListener('scroll', onScroll); });
		};
	}, []);
}
